import moment, { Moment } from 'moment';
import React, { useMemo, useState, useCallback, useEffect } from 'react';
import type { SortBy } from 'tea-component/lib/table/addons';
import FilterCommon, { useFilterCommon } from '@opm/components/FilterCommon';
import MarketContentBody from '@opm/components/MarketContentBody';
import { dealwithDouyinHashtag } from '@opm/components/MarketConnect/utils/videoAndHashtag';
import marketConnect from '@opm/components/MarketConnect';
import CardCommon from '@op/components/CardCommon';
import useIntls from '@opm/hooks/useIntls';
import { UTCDateFilterTips } from '@op/components/BubbleCommon';
import { useDouyinHashtagData } from '../../hooks/useDouyinHashtagData';
import DouyinHashtagTable from './DouyinHashtagTable';
import type { IHashtagTabProps } from '../types';
import FilterBoxCommon from '@op/components/FilterBoxCommon';
import classNames from 'classnames';
import './index.scss';
import SplitPane, { useSplitPane } from '@op/components/SplitPane';
import DouyinHashtagDetail from './DouyinHashtagDetail';
import { useLeftMenuExpand } from '@opm/hooks/useLeftMenuExpand';

const DOUYIN_PRE_REQUEST_ID = 'hotspot/hashtag/douyin/list';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function DouyinTab({
  defaultFilter,
  backCondConfig,
}: IHashtagTabProps & Record<string, any>) {

  const { filter, setFilter, loading, ...filterProps } = useFilterCommon({
    keys: ['SingleDateRange'],
  });
  useIntls();

  // 表格排序状态：受控，由表格列头切换；hook 内据其对完整榜单排序后再本地分页
  const [sort, setSort] = useState<SortBy<any>>({ by: 'heat', order: "desc" });

  const {
    records,
    total,
    loading: dataLoading,
    pageInfo,
    changePageInfo,
    download,
  } = useDouyinHashtagData({ filter, sort });

  const { collapseMenu, expandMenu } = useLeftMenuExpand();

  // 侧边栏状态管理（V2：通过 onOpen/onClose 回调自动处理菜单状态）
  const [selectedHashtag, setSelectedHashtag] = useState<any>(null);
  const { visible: sidebarVisible, show: showSidebar, hide: hideSidebar } =
    useSplitPane(false, { onOpen: collapseMenu, onClose: expandMenu });

  const handleRowClick = useCallback(
    (record: any) => {
      setSelectedHashtag(record);
      showSidebar();
    },
    [showSidebar],
  );

  const handleCloseSidebar = useCallback(() => {
    hideSidebar();
    setSelectedHashtag(null);
  }, [hideSidebar]);

  // 数据变化时关闭侧边栏
  useEffect(() => {
    handleCloseSidebar();
  }, [records]);

  /** UTC+8：禁用今天之后的日期 */
  const disabledDate = useMemo(
    () => ({
      date: (date: Moment) => {
        const todayUtc8 = moment().utcOffset(8).startOf('day');
        return !date
          .clone()
          .utcOffset(8)
          .startOf('day')
          .isAfter(todayUtc8, 'day');
      },
    }),
    [],
  );

  return (
    <MarketContentBody
      loading={loading}
      filterSlot={
        <FilterBoxCommon
          className={classNames('dbom-douyin-hashtag-filter', {
            'dbom-douyin-hashtag-filter--loading': loading,
          })}
          left={[
            {
              key: 'element',
              element: (
                <FilterCommon
                  {...filterProps}
                  userDefaultValue={defaultFilter}
                  realTimeMode
                  filterConfigProps={{
                    SingleDateRange: {
                      tips: UTCDateFilterTips(8),
                      showIcon: false,
                      placement: 'top',
                      showShortcut: false,
                      disabledDate,
                    },
                  }}
                  confirm={(value: any) => {
                    setFilter(value);
                  }}
                  backCondConfig={backCondConfig}
                />
              ),
            },
          ]}
          right={[
            {
              key: 'download',
              style: { marginLeft: 10 },
              event: {
                onClick: () => download(),
              },
            },
          ]}
          onChange={(_value, _key) => {}}
        ></FilterBoxCommon>
      }
    >
      <SplitPane
        sidebarVisible={sidebarVisible}
        sidebar={
          <DouyinHashtagDetail
            data={selectedHashtag}
            onClose={handleCloseSidebar}
          />
        }
      >
        <CardCommon
          className="dbom-douyin-hashtag-card"
          loading={dataLoading}
          loadingHeight={550}
          empty={records.length === 0}
          emptyHeight={550}
        >
          <DouyinHashtagTable
            records={records}
            loading={false}
            total={total}
            pageInfo={pageInfo}
            changePageInfo={changePageInfo}
            sortInfo={[sort, setSort]}
            selectedHashtag={selectedHashtag}
            onRowClick={handleRowClick}
          />
        </CardCommon>
      </SplitPane>
    </MarketContentBody>
  );
}

export default marketConnect<IHashtagTabProps & Record<string, any>>({
  ids: [DOUYIN_PRE_REQUEST_ID],
  dealwith: dealwithDouyinHashtag(DOUYIN_PRE_REQUEST_ID),
})(DouyinTab);
